import { createServerFn } from "@tanstack/react-start";
import { publicSupabase } from "@/lib/supabase-public";
import { z } from "zod";
import { requireAuth as requireSupabaseAuth } from "@/lib/require-auth";
import type { Database } from "@/integrations/supabase/types";
import { inspectionAddressSchema, formatInspectionAddress } from "@/lib/inspection-address";
import {
  attributionNoteLines,
  leadSourceFor,
  metaAttributionSchema,
} from "@/lib/meta-attribution";

// Strip control characters and collapse runaway whitespace.
const clean = (s: string) =>
  s
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .replace(/\s{3,}/g, "  ")
    .trim();

const countUrls = (s: string) => (s.match(/https?:\/\/|www\.|\[url|<a\s/gi) ?? []).length;

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(2)
    .max(100)
    .transform(clean)
    .refine((v) => !/[<>]/.test(v), { message: "Name contains invalid characters" })
    .refine((v) => countUrls(v) === 0, { message: "Name cannot contain a link" }),
  phone: z
    .string()
    .trim()
    .min(7)
    .max(40)
    .refine((v) => (v.replace(/\D/g, "").length >= 7), {
      message: "Please enter a valid phone number",
    }),
  email: z.string().trim().email().max(255).optional().or(z.literal("")),
  service: z.string().max(100).optional().or(z.literal("")),
  message: z
    .string()
    .max(2000)
    .optional()
    .or(z.literal(""))
    .transform((v) => (v ? clean(v) : v))
    .refine((v) => !v || countUrls(v) <= 2, {
      message: "Please remove the extra links from your message",
    }),
  source: z.string().max(100).optional().or(z.literal("")),
  source_page: z.string().max(500).optional().or(z.literal("")),
  // Bot traps: hidden honeypot field + form mount timestamp (ms since epoch).
  hp: z.string().max(200).optional().or(z.literal("")),
  t: z.number().int().optional().nullable(),
  attribution: metaAttributionSchema,
  property_address: inspectionAddressSchema.optional(),
});

const inspectionLeadSchema = schema.superRefine((data, ctx) => {
  if (data.service === "Hail Damage Roof Inspection" && !data.property_address) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["property_address"], message: "Property address is required for a hail inspection." });
  }
});

const LEAD_NOTIFICATION_RECIPIENTS = ['bforstie@gmail.com', 'tim@durafoaminc.com', 'quality@durafoaminc.com']

type LeadEmailInput = {
  leadId: string;
  name: string;
  phone: string;
  email: string | null;
  service: string | null;
  message: string | null;
  source: string | null;
  source_page: string | null;
};

async function logLeadEmail(
  leadId: string,
  status: "sent" | "failed",
  errorMessage?: string,
) {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await (supabaseAdmin as any).from("email_send_log").insert(
      LEAD_NOTIFICATION_RECIPIENTS.map((recipient) => ({
        template_name: "lead-notification",
        recipient_email: recipient,
        status,
        error_message: errorMessage ? errorMessage.slice(0, 1000) : null,
        metadata: { lead_id: leadId },
      })),
    );
  } catch (e) {
    console.error("[leads] failed to write email_send_log", e);
  }
}

async function sendLeadNotifications(lead: LeadEmailInput, _opts?: { resendSuffix?: string }) {
  const { sendGmail } = await import("@/lib/email/gmail-send.server");
  const submittedAt = new Date().toLocaleString("en-US", {
    timeZone: "America/Phoenix",
    dateStyle: "medium",
    timeStyle: "short",
  });
  const lines = [
    `New lead from durafoaminc.com`,
    ``,
    `Name:     ${lead.name}`,
    `Phone:    ${lead.phone}`,
    `Email:    ${lead.email || "(not provided)"}`,
    `Service:  ${lead.service || "(not specified)"}`,
    `Source:   ${lead.source_page || lead.source || "(unknown)"}`,
    `Submitted: ${submittedAt}`,
    ``,
    `Message:`,
    lead.message || "(no message)",
    ``,
    `---`,
    `Lead ID: ${lead.leadId}`,
    `Admin:   https://www.durafoaminc.com/admin`,
  ];
  try {
    await sendGmail({
      to: LEAD_NOTIFICATION_RECIPIENTS,
      subject: `New Durafoam lead: ${lead.name}${lead.service ? ` — ${lead.service}` : ""}`,
      text: lines.join("\n"),
      replyTo: lead.email || undefined,
      fromName: "Durafoam Website",
    });
    await logLeadEmail(lead.leadId, "sent");
  } catch (e) {
    await logLeadEmail(lead.leadId, "failed", e instanceof Error ? e.message : String(e));
    throw e;
  }
}

/**
 * Bot traps. A single tripped signal is NOT enough to discard a submission —
 * browser autofill and password managers can fill hidden decoy fields for real
 * people, and a fast typist can beat a naive timing check. We only drop when
 * BOTH signals fire; a lone signal is logged and flagged but the lead is kept.
 */
const MIN_FILL_MS = 1200;

type GuardInput = { hp?: string | null; t?: number | null };

function evaluateGuard(label: string, g: GuardInput) {
  const decoyFilled = !!(g.hp && g.hp.trim().length > 0);
  const elapsed = typeof g.t === "number" ? Date.now() - g.t : null;
  const tooFast = elapsed !== null && elapsed >= 0 && elapsed < MIN_FILL_MS;
  const drop = decoyFilled && tooFast;
  const signals = [decoyFilled ? "decoy_filled" : null, tooFast ? "too_fast" : null].filter(
    Boolean,
  ) as string[];
  if (signals.length > 0) {
    // No personal data logged — only which trap fired and how long the fill took.
    console.warn(
      `[${label}] bot signals=${signals.join(",")} elapsed_ms=${elapsed ?? "unknown"} action=${
        drop ? "dropped" : "kept"
      }`,
    );
  }
  return { drop, signals, elapsed };
}

export const submitLead = createServerFn({ method: "POST" })
  .inputValidator((input) => inspectionLeadSchema.parse(input))
  .handler(async ({ data }) => {
    // Return success so bots don't retry, but store/send nothing.
    const guard = evaluateGuard("submitLead", data);
    if (guard.drop) return { ok: true, stored: false as const, event_id: null, lead_id: null };

    const url = process.env.SUPABASE_URL;
    const key = process.env.SUPABASE_PUBLISHABLE_KEY;
    if (!url || !key) throw new Error("Server not configured");
    const leadId = crypto.randomUUID();
    const supabase = await publicSupabase();
    if (!supabase) throw new Error("Server not configured");
    const source = leadSourceFor(data.attribution, data.source || "");
    const message = [data.message || "", attributionNoteLines(data.attribution).join("\n")]
      .filter(Boolean)
      .join("\n\n")
      .slice(0, 2000);
    const { error } = await supabase
      .from("leads")
      .insert({
        id: leadId,
        name: data.name,
        phone: data.phone,
        email: data.email || null,
        service: data.service || null,
        message: message || null,
        source: source || null,
        source_page: data.source_page || null,
        ...(data.property_address ? { address: formatInspectionAddress(data.property_address) } : {}),
      });
    if (error) throw new Error(error.message);

    // Fire notification emails (don't fail the submission if email enqueue fails)
    try {
      await sendLeadNotifications({
        leadId,
        name: data.name,
        phone: data.phone,
        email: data.email || null,
        service: data.service || null,
        message: message || null,
        source: source || null,
        source_page: data.source_page || null,
      });
    } catch (e) {
      console.error("[submitLead] notification email failed", e);
    }

    // Lead is stored — now (and only now) emit the Meta conversion event.
    const eventId = crypto.randomUUID();
    try {
      const { getRequest } = await import("@tanstack/react-start/server");
      const { sendCapiEvent, clientIpFromHeaders } = await import("@/lib/meta-capi.server");
      const req = getRequest();
      const origin = req?.headers.get("origin") || "https://www.durafoaminc.com";
      const a = data.attribution ?? {};
      await sendCapiEvent({
        eventName: "Lead",
        eventId,
        eventSourceUrl: `${origin}${data.source_page || a.landing_page || "/"}`,
        email: data.email || null,
        phone: data.phone,
        fbp: a.fbp || null,
        fbc: a.fbc || null,
        clientUserAgent: req?.headers.get("user-agent") ?? null,
        clientIpAddress: req ? clientIpFromHeaders(req.headers) : null,
        customData: {
          ...(a.funnel ? { funnel: a.funnel } : {}),
          ...(a.lead_type ? { lead_type: a.lead_type } : {}),
          ...(a.form_name ? { form_name: a.form_name } : {}),
        },
      });
    } catch (e) {
      console.error("[submitLead] CAPI Lead failed", e);
    }

    return { ok: true, stored: true as const, event_id: eventId, lead_id: leadId };
  });

// ---------------------------------------------------------------------------
// Paid-ad landing page lead (/monsoon-roof-inspection)
// Stores into the same `leads` table, then returns an event_id so the browser
// can fire the Meta Lead pixel event deduplicated with the server CAPI event.
// ---------------------------------------------------------------------------

const attr = z.string().trim().max(500).optional().or(z.literal(""));

const landingSchema = z.object({
  name: schema.shape.name,
  phone: schema.shape.phone,
  email: schema.shape.email,
  zip: z
    .string()
    .trim()
    .min(3)
    .max(10)
    .refine((v) => /^[0-9-]+$/.test(v), { message: "Please enter a valid ZIP code" }),
  leak: z.enum(["Yes", "No", "Not sure"]),
  message: z
    .string()
    .max(1000)
    .optional()
    .or(z.literal(""))
    .transform((v) => (v ? clean(v) : v))
    .refine((v) => !v || countUrls(v) <= 2, {
      message: "Please remove the extra links from your message",
    }),
  hp: schema.shape.hp,
  t: schema.shape.t,
  attribution: z
    .object({
      landing_page: attr,
      referrer: attr,
      utm_source: attr,
      utm_medium: attr,
      utm_campaign: attr,
      utm_content: attr,
      utm_term: attr,
      fbclid: attr,
      oppref: attr,
      fbp: attr,
      fbc: attr,
    })
    .partial()
    .optional(),
});

export const submitLandingLead = createServerFn({ method: "POST" })
  .inputValidator((input) => landingSchema.parse(input))
  .handler(async ({ data }) => {
    const guard = evaluateGuard("submitLandingLead", data);
    if (guard.drop) {
      // No lead stored -> no Lead conversion event.
      return {
        ok: true as const,
        stored: false as const,
        event_id: null,
        lead_id: null,
        dropped: true as const,
        botSignals: guard.signals,
      };
    }


    const url = process.env.SUPABASE_URL;
    const key = process.env.SUPABASE_PUBLISHABLE_KEY;
    if (!url || !key) throw new Error("Server not configured");

    const a = data.attribution ?? {};
    const submittedAt = new Date().toISOString();
    const detailLines = [
      `Currently experiencing a leak: ${data.leak}`,
      `ZIP: ${data.zip}`,
      ...(data.message ? [``, `Visitor note: ${data.message}`] : []),
      ``,
      `— Campaign attribution —`,
      `landing_page: ${a.landing_page || "(none)"}`,
      `referrer: ${a.referrer || "(none)"}`,
      `utm_source: ${a.utm_source || "(none)"}`,
      `utm_medium: ${a.utm_medium || "(none)"}`,
      `utm_campaign: ${a.utm_campaign || "(none)"}`,
      `utm_content: ${a.utm_content || "(none)"}`,
      `utm_term: ${a.utm_term || "(none)"}`,
      `fbclid: ${a.fbclid || "(none)"}`,
      `submitted_at: ${submittedAt}`,
    ];

    const leadId = crypto.randomUUID();
    const supabase = (await publicSupabase())!;
    const { error } = await supabase.from("leads").insert({
      id: leadId,
      name: data.name,
      phone: data.phone,
      email: data.email || null,
      service: "Free Monsoon Roof Inspection",
      message: detailLines.join("\n").slice(0, 2000),
      source: a.utm_source ? `paid-ads:${a.utm_source}` : "paid-ads:meta",
      source_page: a.landing_page || "/monsoon-roof-inspection",
    });
    if (error) throw new Error(error.message);

    try {
      await sendLeadNotifications({
        leadId,
        name: data.name,
        phone: data.phone,
        email: data.email || null,
        service: "Free Monsoon Roof Inspection",
        message: detailLines.join("\n"),
        source: a.utm_source ? `paid-ads:${a.utm_source}` : "paid-ads:meta",
        source_page: a.landing_page || "/monsoon-roof-inspection",
      });
    } catch (e) {
      console.error("[submitLandingLead] notification email failed", e);
    }

    // Lead is stored — now (and only now) emit conversion events.
    const eventId = crypto.randomUUID();
    let capi: { status: string; error?: string } = { status: "skipped" };
    try {
      const { getRequest } = await import("@tanstack/react-start/server");
      const { sendCapiLead, clientIpFromHeaders } = await import("@/lib/meta-capi.server");
      const req = getRequest();
      const origin = req?.headers.get("origin") || "https://www.durafoaminc.com";
      const result = await sendCapiLead({
        eventId,
        eventSourceUrl: `${origin}${a.landing_page || "/monsoon-roof-inspection"}`,
        email: data.email || null,
        phone: data.phone,
        zip: data.zip,
        fbp: a.fbp || null,
        fbc: a.fbc || null,
        clientUserAgent: req?.headers.get("user-agent") ?? null,
        clientIpAddress: req ? clientIpFromHeaders(req.headers) : null,
      });
      capi = { status: result.status, ...("error" in result ? { error: result.error } : {}) };
    } catch (e) {
      console.error("[submitLandingLead] CAPI failed", e);
      capi = { status: "error", error: e instanceof Error ? e.message : "unknown" };
    }

    return {
      ok: true as const,
      stored: true as const,
      event_id: eventId,
      lead_id: leadId,
      capi,
      dropped: false as const,
      botSignals: guard.signals,
    };

  });


export type AdminLead = {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  service: string | null;
  message: string | null;
  source: string | null;
  source_page: string | null;
  contacted_at: string | null;
  created_at: string;
  contact_consent: boolean | null;
};

const listSchema = z.object({
  status: z.enum(["all", "new", "contacted"]).default("all"),
  source_page: z.string().max(500).optional().nullable(),
  since: z.string().optional().nullable(),
  until: z.string().optional().nullable(),
  limit: z.number().int().min(1).max(1000).default(200),
});

export const adminListLeads = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => listSchema.parse(input ?? {}))
  .handler(async ({ data, context }): Promise<AdminLead[]> => {
    const { supabase, userId } = context;
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    let q = supabase
      .from("leads")
      .select("id,name,phone,email,service,message,source,source_page,contacted_at,created_at,contact_consent")
      .order("created_at", { ascending: false })
      .limit(data.limit);
    if (data.status === "new") q = q.is("contacted_at", null);
    else if (data.status === "contacted") q = q.not("contacted_at", "is", null);
    if (data.source_page) q = q.eq("source_page", data.source_page);
    if (data.since) q = q.gte("created_at", data.since);
    if (data.until) q = q.lte("created_at", data.until);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return (rows ?? []) as AdminLead[];
  });

export const adminMarkLeadContacted = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ id: z.string().uuid(), contacted: z.boolean() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const { error } = await supabase
      .from("leads")
      .update({ contacted_at: data.contacted ? new Date().toISOString() : null })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminDeleteLead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const { error } = await supabase.from("leads").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminLeadStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const startMonth = new Date();
    startMonth.setDate(1);
    startMonth.setHours(0, 0, 0, 0);
    const [{ count: total }, { count: monthly }, { count: uncontacted }] = await Promise.all([
      supabase.from("leads").select("id", { count: "exact", head: true }),
      supabase.from("leads").select("id", { count: "exact", head: true }).gte("created_at", startMonth.toISOString()),
      supabase.from("leads").select("id", { count: "exact", head: true }).is("contacted_at", null),
    ]);
    return {
      total: total ?? 0,
      monthly: monthly ?? 0,
      uncontacted: uncontacted ?? 0,
    };
  });

export type LeadEmailStatus = {
  recipient: string;
  status: "pending" | "sent" | "failed" | "suppressed" | "dlq" | "bounced" | "complained";
  error_message: string | null;
  created_at: string;
};

export const adminGetLeadEmailStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ leadId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }): Promise<LeadEmailStatus[]> => {
    const { supabase, userId } = context;
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await (supabaseAdmin as any)
      .from("email_send_log")
      .select("message_id, recipient_email, status, error_message, created_at, metadata")
      .eq("template_name", "lead-notification")
      .contains("metadata", { lead_id: data.leadId })
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    // Keep only the latest row per recipient (rows are newest-first).
    const latest = new Map<string, any>();
    for (const r of (rows ?? []) as any[]) {
      const key = r.message_id || r.recipient_email;
      if (!key) continue;
      if (!latest.has(key)) latest.set(key, r);
    }
    return Array.from(latest.values()).map((r) => ({
      recipient: r.recipient_email,
      status: r.status,
      error_message: r.error_message,
      created_at: r.created_at,
    }));
  });

export const adminResendLeadNotification = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
    if (!isAdmin) throw new Error("Forbidden");
    const { data: lead, error } = await supabase
      .from("leads")
      .select("id,name,phone,email,service,message,source,source_page")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!lead) throw new Error("Lead not found");
    await sendLeadNotifications(
      {
        leadId: lead.id,
        name: lead.name,
        phone: lead.phone,
        email: lead.email,
        service: lead.service,
        message: lead.message,
        source: lead.source,
        source_page: lead.source_page,
      },
      { resendSuffix: `resend-${Date.now()}` },
    );
    return { ok: true };
  });
